import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import {
    invalidateLeasingSeasonDetail,
    invalidateLeasingSeasonForCampaign,
} from '../../containers/LeasingSeason/hooks/leasingSeasonCache'
import {
    Campaign,
    CampaignStep,
    CampaignStepAttachment,
    CampaignStatus,
    CampaignStepType,
    CampaignProspect,
    CampaignProspectActivity,
} from '../../models/Outreach/types'
import {
    SequenceItem,
    SequenceStep,
    DelayStep,
} from '../../containers/Outbound/Campaign/CreateCampaign/types'

// AI Generation types
export interface AIGeneratedEmail {
    subject: string
    content: string
}

export interface AIGenerationRequest {
    campaign_name?: string
    campaign_goal?: string
    num_steps?: number
    channel_type?: 'email' | 'sms' | 'both'
    tone?: string
    prompt?: string
}

// Response can be either a direct array or an object with emails/steps/data property
export type AIGenerationResponse =
    | AIGeneratedEmail[]
    | {
          emails?: AIGeneratedEmail[]
          steps?: AIGeneratedEmail[]
          data?: AIGeneratedEmail[]
      }

// Backend request interfaces
export interface StepScheduleRequest {
    day_of_week: number // 0-6 (0=Sunday, 6=Saturday)
    enabled: boolean
    start_time: string // Format: "08:00 AM"
    end_time: string // Format: "06:00 PM"
}

export interface DelayStepRequest {
    step_order: number
    step_type: 'DELAY'
    delay_days: number
}

export interface UpdateDelayStepRequest {
    id?: number
    step_order: number
    step_type: 'DELAY'
    delay_days: number
}

export interface EmailStepRequest {
    step_order: number
    step_type: 'EMAIL'
    content: string
    content_html?: string
    subject: string
    schedule: StepScheduleRequest[]
    guest_card?: number | null
}

export interface UpdateEmailStepRequest {
    id?: number
    step_order: number
    step_type: 'EMAIL'
    content: string
    content_html?: string
    subject: string
    schedule: StepScheduleRequest[]
    guest_card?: number | null
}

export interface TextMessageStepRequest {
    step_order: number
    step_type: 'TEXT_MESSAGE'
    content: string
    schedule: StepScheduleRequest[]
    guest_card?: number | null
}

export interface UpdateTextMessageStepRequest {
    id?: number
    step_order: number
    step_type: 'TEXT_MESSAGE'
    content: string
    schedule: StepScheduleRequest[]
    guest_card?: number | null
}

export type CampaignStepRequest =
    | DelayStepRequest
    | EmailStepRequest
    | TextMessageStepRequest

export type UpdateCampaignStepRequest =
    | UpdateDelayStepRequest
    | UpdateEmailStepRequest
    | UpdateTextMessageStepRequest

export interface CreateCampaignRequest {
    name: string
    campaign_goal?: string
    steps: CampaignStepRequest[]
    reply_to_email: string
    sender_name: string
    leasing_season_id?: number
    concessions?: number[] | null
}

export interface UpdateCampaignRequest {
    name?: string
    campaign_goal?: string
    steps?: UpdateCampaignStepRequest[]
    reply_to_email?: string
    sender_name?: string
    concessions?: number[] | null
}

const isDelayStep = (item: SequenceItem): item is DelayStep => {
    return item.type === 'delay'
}

const isMessageStep = (item: SequenceItem): item is SequenceStep => {
    return item.type === 'email' || item.type === 'sms'
}

// Transform frontend sequence format to backend format
const transformSequenceToBackend = (
    sequence: SequenceItem[],
): CampaignStepRequest[] => {
    return sequence.map((item, index) => {
        const stepOrder = index + 1

        if (isDelayStep(item)) {
            const delayStep: DelayStepRequest = {
                step_order: stepOrder,
                step_type: 'DELAY',
                delay_days: item.days,
            }
            return delayStep
        }

        // Message step (email or sms)
        const messageStep = item as SequenceStep

        // Transform schedule from WeeklySchedule to array format
        // Backend expects all 7 days, so we send all days with enabled/disabled flags
        const dayMap: Record<string, number> = {
            sun: 0,
            mon: 1,
            tue: 2,
            wed: 3,
            thu: 4,
            fri: 5,
            sat: 6,
        }

        const schedule: StepScheduleRequest[] = messageStep.schedule
            ? Object.entries(messageStep.schedule).map(
                  ([day, daySchedule]) => ({
                      day_of_week: dayMap[day],
                      enabled: daySchedule.enabled,
                      start_time: daySchedule.startTime,
                      end_time: daySchedule.endTime,
                  }),
              )
            : // If no schedule provided, create disabled entries for all days
              Object.values(dayMap).map((dayOfWeek) => ({
                  day_of_week: dayOfWeek,
                  enabled: false,
                  start_time: '12:00 AM',
                  end_time: '12:00 AM',
              }))

        if (messageStep.type === 'email') {
            const emailStep: EmailStepRequest = {
                step_order: stepOrder,
                step_type: 'EMAIL',
                content: messageStep.message || '',
                content_html: messageStep.content_html || undefined,
                subject: messageStep.subject || '',
                schedule: schedule,
                guest_card: messageStep.guest_card || null,
            }
            return emailStep
        } else {
            const textMessageStep: TextMessageStepRequest = {
                step_order: stepOrder,
                step_type: 'TEXT_MESSAGE',
                content: messageStep.message || '',
                schedule: schedule,
                guest_card: messageStep.guest_card || null,
            }
            return textMessageStep
        }
    })
}

// Transform CampaignStep[] to backend update format (includes id for existing steps)
const transformStepsToUpdateFormat = (
    steps: CampaignStep[],
): UpdateCampaignStepRequest[] => {
    return steps.map((step) => {
        const stepRequest: any = {
            id: step.id,
            step_order: step.step_order,
            step_type: step.step_type,
        }

        if (step.step_type === CampaignStepType.DELAY) {
            stepRequest.delay_days = step.delay_days || 0
        } else {
            // EMAIL or TEXT_MESSAGE
            stepRequest.content = step.content || ''

            if (step.step_type === CampaignStepType.EMAIL) {
                stepRequest.subject = step.subject || ''
                if (step.content_html) {
                    stepRequest.content_html = step.content_html
                }
            }

            // Transform schedule from CampaignStepSchedule[] to StepScheduleRequest[]
            const schedule: StepScheduleRequest[] = step.schedule
                ? step.schedule.map((scheduleItem) => ({
                      day_of_week: scheduleItem.day_of_week,
                      enabled: scheduleItem.enabled,
                      start_time: scheduleItem.start_time,
                      end_time: scheduleItem.end_time,
                  }))
                : // If no schedule provided, create disabled entries for all days
                  [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
                      day_of_week: dayOfWeek,
                      enabled: false,
                      start_time: '12:00 AM',
                      end_time: '12:00 AM',
                  }))

            stepRequest.schedule = schedule
            stepRequest.guest_card = step.guest_card || null
        }

        return stepRequest as UpdateCampaignStepRequest
    })
}

export const useCampaign = () => {
    const queryClient = useQueryClient()

    const {
        data: campaigns,
        isLoading: isLoadingCampaigns,
        error: errorCampaigns,
    } = useQuery<AxiosResponse<Campaign[]>, Error>({
        queryKey: ['campaigns'],
        queryFn: () => axiosInstance.get(`/outreach/campaigns/`),
        enabled: true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createCampaign = useMutation({
        mutationFn: ({
            name,
            campaignGoal,
            sequence,
            replyToEmail,
            senderName,
            leasingSeasonId,
            concessions,
        }: {
            name: string
            campaignGoal?: string
            sequence: SequenceItem[]
            replyToEmail: string
            senderName: string
            leasingSeasonId?: number
            concessions?: number[] | null
        }) => {
            const steps = transformSequenceToBackend(sequence)

            const payload: CreateCampaignRequest = {
                name,
                steps,
                reply_to_email: replyToEmail,
                sender_name: senderName,
            }

            // Only include campaign_goal if provided
            if (campaignGoal) {
                payload.campaign_goal = campaignGoal
            }

            if (leasingSeasonId != null) {
                payload.leasing_season_id = leasingSeasonId
            }

            if (concessions !== undefined) {
                payload.concessions = concessions
            }

            return axiosInstance.post<Campaign>('/outreach/campaigns/', payload)
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns'],
            })
            if (variables.leasingSeasonId != null) {
                invalidateLeasingSeasonDetail(
                    queryClient,
                    variables.leasingSeasonId,
                )
            }
            toast.success('Campaign created successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to create campaign'
            toast.error(errorMessage)
        },
    })

    const updateCampaignStatus = useMutation({
        mutationFn: ({
            id,
            status,
        }: {
            id: string | number
            status: CampaignStatus
        }) =>
            axiosInstance.post(`/outreach/campaigns/${id}/update_status/`, {
                status: status,
            }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['campaigns'] })
            queryClient.invalidateQueries({
                queryKey: ['campaigns', variables.id],
            })
            invalidateLeasingSeasonForCampaign(queryClient, variables.id)
            const statusMessage =
                variables.status === CampaignStatus.ACTIVE
                    ? 'resumed'
                    : variables.status === CampaignStatus.PAUSED
                    ? 'paused'
                    : 'archived'
            toast.success(`Campaign ${statusMessage} successfully`)
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update campaign status'
            toast.error(errorMessage)
        },
    })

    const generateAIEmails = useMutation({
        mutationFn: (request: AIGenerationRequest) =>
            axiosInstance.post<AIGenerationResponse>(
                '/outreach/campaigns/ai-campaign-generation/',
                request,
            ),
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to generate AI emails'
            toast.error(errorMessage)
        },
    })

    return {
        campaigns: campaigns?.data,
        isLoadingCampaigns,
        errorCampaigns,
        createCampaign,
        updateCampaignStatus,
        generateAIEmails,
    }
}

export const useCampaignDetail = (campaignId: string | number | undefined) => {
    const queryClient = useQueryClient()

    const {
        data: campaignDetail,
        isLoading: isLoadingCampaignDetail,
        error: errorCampaignDetail,
    } = useQuery<AxiosResponse<Campaign>, Error>({
        queryKey: ['campaigns', campaignId],
        queryFn: () => axiosInstance.get(`/outreach/campaigns/${campaignId}/`),
        enabled: !!campaignId,
    })

    const updateCampaign = useMutation({
        mutationFn: ({
            id,
            name,
            campaignGoal,
            steps,
            rawSteps,
            replyToEmail,
            senderName,
            concessions,
        }: {
            id: string | number
            name?: string
            campaignGoal?: string
            steps?: CampaignStep[]
            rawSteps?: UpdateCampaignStepRequest[]
            replyToEmail?: string
            senderName?: string
            concessions?: number[] | null
        }) => {
            const payload: UpdateCampaignRequest = {}

            if (name !== undefined) {
                payload.name = name
            }

            if (campaignGoal !== undefined) {
                payload.campaign_goal = campaignGoal || undefined
            }

            if (rawSteps !== undefined) {
                payload.steps = rawSteps
            } else if (steps !== undefined) {
                payload.steps = transformStepsToUpdateFormat(steps)
            }

            if (replyToEmail !== undefined) {
                payload.reply_to_email = replyToEmail
            }

            if (senderName !== undefined) {
                payload.sender_name = senderName
            }

            if (concessions !== undefined) {
                payload.concessions = concessions
            }

            return axiosInstance.patch(`/outreach/campaigns/${id}/`, payload)
        },
        onSuccess: (response, variables) => {
            queryClient.setQueryData(['campaigns', variables.id], response)
            queryClient.invalidateQueries({ queryKey: ['campaigns'] })
            queryClient.invalidateQueries({
                queryKey: ['campaigns', variables.id],
            })
            invalidateLeasingSeasonForCampaign(queryClient, variables.id)
            toast.success('Campaign updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update campaign'
            toast.error(errorMessage)
        },
    })

    const deleteCampaign = useMutation({
        mutationFn: (id: string | number) =>
            axiosInstance.delete(`/outreach/campaigns/${id}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['campaigns'] })
            toast.success('Campaign deleted')
        },
        onError: () => {
            toast.error('Failed to delete campaign')
        },
    })

    const updateCampaignStatus = useMutation({
        mutationFn: ({
            id,
            status,
        }: {
            id: string | number
            status: CampaignStatus
        }) =>
            axiosInstance.post(`/outreach/campaigns/${id}/update_status/`, {
                status: status,
            }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['campaigns'] })
            queryClient.invalidateQueries({
                queryKey: ['campaigns', variables.id],
            })
            invalidateLeasingSeasonForCampaign(queryClient, variables.id)
            const statusMessage =
                variables.status === CampaignStatus.ACTIVE
                    ? 'resumed'
                    : variables.status === CampaignStatus.PAUSED
                    ? 'paused'
                    : 'archived'
            toast.success(`Campaign ${statusMessage} successfully`)
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update campaign status'
            toast.error(errorMessage)
        },
    })

    const {
        data: campaignProspects,
        isLoading: isLoadingCampaignProspects,
        error: errorCampaignProspects,
    } = useQuery({
        queryKey: ['campaigns', campaignId, 'prospects'],
        queryFn: () =>
            axiosInstance.get<AxiosResponse<CampaignProspect[]>>(
                `/outreach/campaigns/${campaignId}/prospects/`,
            ),
        enabled: !!campaignId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const {
        data: campaignProspectActivities,
        isLoading: isLoadingCampaignProspectActivities,
        error: errorCampaignProspectActivities,
    } = useQuery({
        queryKey: ['campaigns', campaignId, 'prospect-activities'],
        queryFn: () =>
            axiosInstance.get<AxiosResponse<CampaignProspectActivity[]>>(
                `/outreach/campaigns/${campaignId}/recent-activity/`,
            ),
        enabled: !!campaignId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return {
        campaignDetail: campaignDetail?.data,
        isLoadingCampaignDetail,
        errorCampaignDetail,
        updateCampaign,
        deleteCampaign,
        updateCampaignStatus,
        campaignProspects,
        isLoadingCampaignProspects,
        errorCampaignProspects,
        campaignProspectActivities,
        isLoadingCampaignProspectActivities,
        errorCampaignProspectActivities,
    }
}

// Merge Variables

export interface MergeVariable {
    key: string
    label: string
    category: 'prospect' | 'property' | 'campaign' | 'concession'
}

export const useMergeVariables = () => {
    const {
        data: mergeVariables,
        isLoading: isLoadingMergeVariables,
        error: errorMergeVariables,
    } = useQuery<AxiosResponse<MergeVariable[]>, Error>({
        queryKey: ['campaign-merge-variables'],
        queryFn: () =>
            axiosInstance.get('/outreach/campaigns/merge-variables/'),
        staleTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
    })

    return {
        mergeVariables: mergeVariables?.data || [],
        isLoadingMergeVariables,
        errorMergeVariables,
    }
}

// Attachments

export const useAttachments = (
    campaignId: number | string | undefined,
    stepId: number | string | undefined,
) => {
    const queryClient = useQueryClient()

    const uploadAttachment = useMutation({
        mutationFn: (file: File) => {
            const formData = new FormData()
            formData.append('file', file)
            return axiosInstance.post<CampaignStepAttachment>(
                `/outreach/campaigns/${campaignId}/steps/${stepId}/attachments/`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload attachment'
            toast.error(errorMessage)
        },
    })

    const deleteAttachment = useMutation({
        mutationFn: (attachmentId: number) =>
            axiosInstance.delete(
                `/outreach/campaigns/${campaignId}/steps/${stepId}/attachments/${attachmentId}/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
            })
            toast.success('Attachment deleted')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete attachment'
            toast.error(errorMessage)
        },
    })

    return {
        uploadAttachment,
        deleteAttachment,
    }
}
